'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { deleteTask, getTaskDetail, listOfflineAvatarHistory } from '@/app/apis';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineDownload } from '@/app/components/icons/BaseLineDownload';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLineShare2 } from '@/app/components/icons/BaseLineShare2';
import { Fail } from '@/app/components/icons/Fail';
import { Loading } from '@/app/components/icons/Loading';
import { DownloadButton } from '@/app/components/shared/DownloadButton';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { toast } from '@/app/components/ui/use-toast';
import { API_ORIGIN, resolveApiPath } from '@/app/constants';
import { useFetch, useFetchStream, useGlobalModal, useScreenConfig } from '@/app/hooks';
import type { CreationInfo, TaskInfo } from '@/app/types';
import { generateFileName, getOfflineAvatarTaskCover, getOfflineAvatarTaskStateMessage } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { ShareOfflineAvatarCreationDialog } from './ShareOfflineAvatarCreationDialog';

const TASK_STATE_URL = resolveApiPath('/vidu/v1/tasks/state');
const ACTIVE_STATES = new Set<TaskInfo['state']>(['created', 'queueing', 'preparation', 'scheduling', 'processing']);
const ITEMS_PER_ROW = { sm: 1, lg: 2, qhd: 5, 'qhd-plus': 5, default: 4, placeholder: 4 };

export function OfflineAvatarTaskList() {
  const t = useTranslations('tools.offlineDigitalHuman.studio.taskHistory');
  const tState = useTranslations();
  const tCommon = useTranslations('common');
  const tOperations = useTranslations('creations.operations');
  const tLiveRecords = useTranslations('liveRecords');
  const format = useFormatter();
  const router = useRouter();
  const itemsPerRow = useScreenConfig<number>(ITEMS_PER_ROW);
  const [tasks, setTasks] = useState<TaskInfo[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [shareTarget, setShareTarget] = useState<{ creation: CreationInfo; taskId: string } | null>(null);
  const { open: openConfirmDialog, close: closeConfirmDialog } = useGlobalModal('confirmDialog');
  const { loading } = useFetch(() => listOfflineAvatarHistory({ query: { 'pager.pagesz': 50 } }), {
    onSuccess: ({ data }) => setTasks(data?.tasks ?? []),
    suppressUnauthorizedLoginDialog: true,
  });

  const updateTask = useCallback((id: string, patch: Partial<TaskInfo>) => {
    setTasks((current) => current.map((task) => (task.id === id ? { ...task, ...patch } : task)));
  }, []);

  const handleDelete = (task: TaskInfo, event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    openConfirmDialog({
      title: tOperations('delete'),
      description: t('deleteConfirm'),
      secondaryButtonText: tCommon('dialog.cancel'),
      primaryButtonText: tCommon('dialog.delete'),
      onPrimaryAction: async () => {
        setDeletingId(task.id);
        try {
          const { error } = await deleteTask({ path: { id: task.id } });
          if (error) throw new Error(error.message);
          setTasks((current) => current.filter((item) => item.id !== task.id));
          toast({ type: 'success', message: t('deleteSuccess') });
          closeConfirmDialog();
        } catch {
          toast({ type: 'warning', message: t('deleteFailed') });
        } finally {
          setDeletingId(null);
        }
      },
      onSecondaryAction: closeConfirmDialog,
    });
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="relative min-h-[calc(100dvh-220px)]">
        <GlobalLoader className="top-1/2" />
      </div>
    );
  }

  if (tasks.length === 0) return <SearchEmpty emptyText={t('empty')} />;

  return (
    <div className="grid gap-3 pb-6" style={{ gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))` }}>
      {tasks.map((task) => {
        const creation = task.creations?.[0];
        const stateMessage = getOfflineAvatarTaskStateMessage({ creation, task });
        const stateTitle = stateMessage
          ? stateMessage.titleKey === 'task.status.otherFailed.title'
            ? tState('task.status.offlineAvatarFailed.title')
            : tState(stateMessage.titleKey)
          : '';
        const canOpen = !stateMessage && Boolean(creation?.id);
        const canDownload = canOpen && Boolean(creation?.uri);
        const cover = getOfflineAvatarTaskCover(creation);
        const createdAt = task.created_at
          ? format.dateTime(new Date(task.created_at), {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
          : '';

        return (
          <article
            key={task.id}
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : undefined}
            onClick={canOpen ? () => router.push(`/vidu-stream/creation/${creation?.id}`) : undefined}
            onKeyDown={
              canOpen
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      router.push(`/vidu-stream/creation/${creation?.id}`);
                    }
                  }
                : undefined
            }
            className={`group relative aspect-video overflow-hidden rounded-8 bg-system-bg02 ${
              canOpen ? 'cursor-pointer' : ''
            }`}
          >
            {ACTIVE_STATES.has(task.state) ? <TaskStateWatcher taskId={task.id} onUpdate={updateTask} /> : null}
            {cover ? (
              <img src={cover} alt="" className="size-full object-contain" />
            ) : (
              <div className="size-full bg-system-white04" />
            )}
            {stateMessage ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 px-4 text-center">
                {stateMessage.loading === 'queueing' ? (
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(255,255,255,0.12),transparent_55%)]" />
                ) : null}
                {stateMessage.loading === 'processing' ? (
                  <Loading className="relative z-10 size-5 animate-spin" />
                ) : null}
                {stateMessage.icon === 'fail' ? <Fail className="relative z-10 text-2xl text-system-white64" /> : null}
                <p className="relative z-10 font-medium text-sm text-system-white80">{stateTitle}</p>
                {stateMessage.contentKey ? (
                  <p className="relative z-10 line-clamp-2 text-system-white48 text-xs">
                    {stateMessage.contentKey === 'task.status.auditVideoFailed.content'
                      ? t('auditFailedContent')
                      : tState(stateMessage.contentKey)}
                  </p>
                ) : null}
              </div>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={tLiveRecords('card.more')}
                  onClick={(event) => event.stopPropagation()}
                  className="absolute top-2 right-2 z-10 flex size-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition hover:bg-black/70"
                >
                  <BaseLineMore className="size-4" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[132px]" onClick={(event) => event.stopPropagation()}>
                {canDownload && creation ? (
                  <DropdownMenuItem onSelect={(event) => event.preventDefault()} className="relative gap-2">
                    <BaseLineDownload className="size-4" />
                    {tOperations('download')}
                    <DownloadButton
                      creationType={creation.type}
                      waterMarkUri={creation.uri || ''}
                      noWaterMarkUri={creation.nomark_uri}
                      downloadFileName={generateFileName({ type: creation.type, id: creation.id })}
                      directWatermarkedDownload
                      taskId={task.id}
                      creationId={creation.id}
                      hasCopyright={creation.has_copyright}
                      className="absolute inset-0 opacity-0"
                    />
                  </DropdownMenuItem>
                ) : null}
                {canOpen && creation ? (
                  <DropdownMenuItem className="gap-2" onSelect={() => setShareTarget({ creation, taskId: task.id })}>
                    <BaseLineShare2 className="size-4" />
                    {tOperations('share')}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  disabled={deletingId === task.id}
                  className="gap-2 text-red-500 focus:text-red-500"
                  onSelect={(event) => handleDelete(task, event)}
                >
                  <BaseLineDelete className="size-4" />
                  {tOperations('delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {createdAt ? (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-3 py-2 text-right text-white/80 text-xs">
                {createdAt}
              </div>
            ) : null}
          </article>
        );
      })}
      {shareTarget ? (
        <ShareOfflineAvatarCreationDialog
          open
          creationId={shareTarget.creation.id}
          taskId={shareTarget.taskId}
          onOpenChange={(open) => !open && setShareTarget(null)}
        />
      ) : null}
    </div>
  );
}

function TaskStateWatcher({
  taskId,
  onUpdate,
}: {
  taskId: string;
  onUpdate: (id: string, patch: Partial<TaskInfo>) => void;
}) {
  const keepWatchingRef = useRef(true);
  const { run, close } = useFetchStream<{ id: string }>(TASK_STATE_URL, {
    baseURL: API_ORIGIN,
    onMessage: (message) => {
      for (const chunk of message.split('data:')) {
        try {
          const state = JSON.parse(chunk.trim()) as Pick<TaskInfo, 'state' | 'err_code' | 'err_msg'>;
          if (!state.state) continue;
          keepWatchingRef.current = ACTIVE_STATES.has(state.state);
          if (!keepWatchingRef.current) {
            close();
            if (state.state === 'success') {
              onUpdate(taskId, { state: 'processing' });
              void getTaskDetail({ path: { id: taskId } }).then(({ data }) =>
                onUpdate(taskId, data ?? { state: 'success' }),
              );
            } else {
              onUpdate(taskId, state);
            }
          } else {
            onUpdate(taskId, state);
          }
        } catch {
          // SSE may contain heartbeat or partial chunks.
        }
      }
    },
    onClose: () => {
      if (keepWatchingRef.current) run({ id: taskId });
    },
  });

  useEffect(() => {
    keepWatchingRef.current = true;
    run({ id: taskId });
    return () => {
      keepWatchingRef.current = false;
      close();
    };
  }, [close, run, taskId]);

  return null;
}
